import { Box, Tooltip, Typography } from "@mui/material";
import React from "react";

import { monospaceStyle } from "../../theme";
import { useSourceJump } from "./sourceJump";

export interface SourceMarkProps {
  // The number of the source in the view's source list; null when the list
  // does not hold the source
  number: number | null;
  // What the source states about the fact
  statement?: string | null;
  // The quoted passage
  quote?: string | null;
  title?: string | null;
  site?: string | null;
  // The publication date, YYYY-MM-DD
  date?: string | null;
}

const markSx = {
  ...monospaceStyle,
  fontSize: "0.7em",
  lineHeight: 1,
  verticalAlign: "super",
  color: "link.main",
  background: "none",
  border: 0,
  p: 0,
  mx: "1px",
} as const;

// A mark reads "[n]", so that marks side by side do not run together
const markText = (number: number | null): string => `[${number ?? "•"}]`;

/**
 * The number of a source, raised and bracketed, beside a fact.
 *
 * Pointed at, it shows what the source states and the quoted passage.
 * Clicked, it leads to the row of the source in the view's source table.
 */
const SourceMark: React.FC<SourceMarkProps> = ({
  number,
  statement,
  quote,
  title,
  site,
  date,
}) => {
  const jump = useSourceJump();
  const origin = [site, date].filter(Boolean).join(" · ");

  const passage = (
    <Box sx={{ display: "flex", flexDirection: "column", gap: 0.5, py: 0.25 }}>
      {statement && <Typography variant="body2">{statement}</Typography>}
      {quote && (
        <Typography variant="body2" sx={{ fontStyle: "italic" }}>
          “{quote}”
        </Typography>
      )}
      {!statement && !quote && title && (
        <Typography variant="body2">{title}</Typography>
      )}
      {origin && (
        <Typography variant="caption" sx={{ ...monospaceStyle, opacity: 0.8 }}>
          {origin}
        </Typography>
      )}
    </Box>
  );

  return (
    <Tooltip title={passage} arrow placement="top">
      {number !== null && jump ? (
        <Box
          component="button"
          type="button"
          aria-label={`Source ${number}`}
          onClick={(event: React.MouseEvent) => {
            // A mark sits inside rows and cards that are clickable themselves
            event.stopPropagation();
            jump(number);
          }}
          sx={{
            ...markSx,
            cursor: "pointer",
            "&:hover": { textDecoration: "underline" },
          }}
        >
          {markText(number)}
        </Box>
      ) : (
        <Box component="span" sx={markSx}>
          {markText(number)}
        </Box>
      )}
    </Tooltip>
  );
};

export default SourceMark;
